import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { OperationsService } from '@wdx/clmi/api-services/services';
import { WdxToastService } from '@wdx/shared/components/wdx-toast';
import {
    OperationsSetup,
    Severity,
    SystemEntity,
    TRANSLATION_TOAST_MESSAGE_ACTION_COMPLETED_SUCCESSFULLY,
    TranslationsService,
} from '@wdx/shared/utils';
import { of } from 'rxjs';
import {
    catchError,
    groupBy,
    map,
    mergeMap,
    switchMap,
    tap,
    throttleTime,
} from 'rxjs/operators';
import * as operationsActions from './operations.actions';
import { HttpResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';

@Injectable()
export class OperationsEffects {
    private actions$ = inject(Actions);
    private operationsService = inject(OperationsService);
    private toastService = inject(WdxToastService);
    private translationsService = inject(TranslationsService);

    private readonly THROTTLE_TIME = 2000;

    getOperationsForId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(operationsActions.getOperationsForId),
            groupBy((action) => `${action.entityType}-${action.entityId}`),
            mergeMap((group$) =>
                group$.pipe(
                    throttleTime(this.THROTTLE_TIME),
                    switchMap((action) =>
                        this.getOperationsForIdActions(action),
                    ),
                ),
            ),
        ),
    );

    patchActionWithURL$ = createEffect(() =>
        this.actions$.pipe(
            ofType(operationsActions.patchActionWithURL),
            mergeMap((action) =>
                this.operationsService
                    .patchActionWithURL(
                        action.entityType,
                        action.entityId,
                        action.url,
                        { observe: 'response', responseType: 'blob' },
                    )
                    .pipe(
                        map((response) => {
                            this.saveFile(response);
                            return operationsActions.patchActionWithURLSuccess({
                                entityType: action.entityType,
                                entityId: action.entityId,
                                url: action.url,
                            });
                        }),
                        catchError((error) =>
                            of(
                                operationsActions.patchWithURLFailure({
                                    error,
                                }),
                            ),
                        ),
                    ),
            ),
        ),
    );

    patchActionWithURLSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(operationsActions.patchActionWithURLSuccess),
                tap(() => {
                    this.toastService.show({
                        body: [
                            this.translationsService.getTranslationByKey(
                                TRANSLATION_TOAST_MESSAGE_ACTION_COMPLETED_SUCCESSFULLY,
                            ),
                        ],
                        severity: Severity.Success,
                    });
                }),
            ),
        { dispatch: false },
    );

    getOperationsForEntity$ = createEffect(() =>
        this.actions$.pipe(
            ofType(operationsActions.getOperationsForEntity),
            groupBy((action) => action.entityType),
            mergeMap((group$) =>
                group$.pipe(
                    throttleTime(this.THROTTLE_TIME),
                    switchMap((action) =>
                        this.getOperationsForEntityActions(action.entityType),
                    ),
                ),
            ),
        ),
    );

    getOperationsForIdActions = (action: OperationsSetup) =>
        this.operationsService
            .getOperationsForId(action.entityType, action.entityId)
            .pipe(
                map((operations) =>
                    operationsActions.getOperationsForIdSuccess({
                        entityType: action.entityType,
                        entityId: action.entityId,
                        operations,
                    }),
                ),
                catchError((error) =>
                    of(
                        operationsActions.getOperationsForIdFailure({
                            entityType: action.entityType,
                            entityId: action.entityId,
                            error,
                        }),
                    ),
                ),
            );

    getOperationsForEntityActions = (entityType: SystemEntity) =>
        this.operationsService.getOperationsForEntity(entityType).pipe(
            map((operations) =>
                operationsActions.getOperationsForEntitySuccess({
                    entityType: entityType,
                    operations,
                }),
            ),
            catchError((error) =>
                of(
                    operationsActions.getOperationsForEntityFailure({
                        entityType: entityType,
                        error,
                    }),
                ),
            ),
        );

    private saveFile(response: HttpResponse<Blob>) {
        const contentDisposition = response?.headers?.get(
            'content-disposition',
        );
        if (!contentDisposition) {
            return;
        }
        const filename = contentDisposition
            .split(';')
            .find((val) => val?.trim()?.startsWith('filename='))
            ?.trim()
            ?.replace('filename=', '');

        const contentType = response.headers.get('content-type') as string;
        const blob: Blob = new Blob([response.body as BlobPart], {
            type: contentType,
        });

        saveAs(blob, filename);
    }
}
